import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useApiClient,
  ApiError,
  AdminStatsOut,
  AdminUserOut,
} from '../services/apiClient';

/**
 * Loads the admin user list and dashboard stats together.
 *
 * `useApiClient()` returns a fresh object on every render, so it must never
 * appear in a dependency array — the eslint suppression below is deliberate
 * and is the reason this fetch logic lives in one hook rather than in each
 * component that needs it.
 */
export function useAdminData() {
  const api = useApiClient();
  const [users, setUsers] = useState<AdminUserOut[] | null>(null);
  const [stats, setStats] = useState<AdminStatsOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  // Guard against concurrent refresh() calls resolving out of order
  const requestGeneration = useRef(0);

  const refresh = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    setForbidden(false); // Reset forbidden state on each refresh
    try {
      const [nextStats, nextUsers] = await Promise.all([
        api.getAdminStats(),
        api.getAdminUsers(),
      ]);
      // Only update state if this is still the latest request
      if (generation === requestGeneration.current) {
        setStats(nextStats);
        setUsers(nextUsers);
        setError(null);
      }
    } catch (e) {
      // Only update state if this is still the latest request
      if (generation === requestGeneration.current) {
        if (e instanceof ApiError && e.status === 403) {
          setForbidden(true);
        }
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
      }
    } finally {
      // Always set loading to false if this is still the latest request
      if (generation === requestGeneration.current) {
        setLoading(false);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { users, stats, loading, error, forbidden, refresh };
}
